import { useState, useRef, useEffect } from "react";
import classes from "./Booking.module.css";
import { BookingContainer } from "./BookingContainer";
import Modal from "../../components/UI/Modal";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useStudioAvailabilities } from "../../hooks/calendar/useStudioAvailabilities";
import { apiDayToCanonicalDay, jsDayToApiDay } from "../../util/studioAvailability";
// import { useTimeBlockingLogic } from '../../hooks/calendar/useTimeBlockingLogic';

export const Booking = ({ 
    data, 
    isLoading, 
    error, 
    selectedBookingDate, 
    onClose, 
    selectedStudioId,
    hasStudios,
    onBookingUpdate,
    blockedTimes,
    blockedTimesError,
    createBlockedTime,
    deleteBlockedTime,
    // fromPhone
  }) => {

  const { availabilities, loading: availabilitiesLoading } = useStudioAvailabilities(selectedStudioId);

  // const [showBlockingPhone, setShowBlockingPhone] = useState(false);  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [modalClass, setModalClass] = useState('modalBlocking');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullDay, setIsFullDay] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const modalRef = useRef(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getSelectedDateBookings = () => {
    if (!selectedBookingDate || !data) return [];
    
    const studio = data.find(s => s.studioId === selectedBookingDate?.studioId);
    if (!studio) return [];
  
    const filteredBookings = studio?.bookings
      .filter(booking => 
        booking.date === selectedBookingDate.date && 
        booking.status !== "CANCELLED" &&
        booking.status !== "DECLINED"
      )
      .map(booking => ({
        ...booking,
        studioImage: studio.image,
        studioName: studio.name,
        id: booking.id
      }))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    return filteredBookings;
  };

  const bookings = getSelectedDateBookings();

  const blockedTimeItems = (blockedTimes?.blockedTimesByStudioID?.items || []).filter(blockedTime => {
    const now = Date.now();
    const startDate = new Date(blockedTime?.startDateTime);
    const endDate = new Date(blockedTime?.endDateTime);
  
    const selectedDateParts = selectedBookingDate?.date?.split('.');
    const selectedDate = selectedDateParts
      ? new Date(`${selectedDateParts[2]}-${selectedDateParts[1]}-${selectedDateParts[0]}`)
      : null;
  
    const normalize = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
    return (
      endDate.getTime() > now &&
      selectedDate &&
      normalize(startDate) <= normalize(selectedDate) &&
      normalize(endDate) >= normalize(selectedDate)
    );
  });

  useEffect(() => {
    localStorage.setItem('blockedTimes', JSON.stringify(blockedTimes));
  }, [blockedTimes]);

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !event.target.closest(`.${modalClass}`)
      ) {
        setIsModalOpen(false);
        setStartTime(null);
        setEndTime(null);
        setIsFullDay(false);
        setIsNotAvailable(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalOpen, modalClass]);

  const handleConfirmation = async () => {
    // Handle 24:00 case by converting to 23:59
    const adjustedEndTime = endTime === "24:00" ? "23:59" : endTime;
    
    if (isFullDay && bookings.length > 0) {
      setModalClass('modalBlockingConfirmation');
      setShowConfirmation(true);
      return;
    }
    
    // Get the currently selected date
    const [day, month, year] = selectedBookingDate.date.split('.');
    const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    
    // Get the input value
    const inputValue = document.querySelector(`.${classes.input}`)?.value || "Blocked Time";
    
    // Create blocked time object with adjusted end time
    const blockedTimeInput = {
      studioID: selectedStudioId,
      title: inputValue,
      startDateTime: new Date(`${formattedDate}T${isFullDay ? '00:00' : startTime}`).toISOString(),
      endDateTime: new Date(`${formattedDate}T${isFullDay ? '23:59' : adjustedEndTime}`).toISOString(),
      isFullDay: isFullDay
    };
    
    try {
      // Call the API directly
      await createBlockedTime(blockedTimeInput);
      
      // Reset UI on success
      setIsModalOpen(false);
      setStartTime(null);
      setEndTime(null);
      setIsFullDay(false);
      setIsNotAvailable(false);
      setShowConfirmation(false);
      setModalClass('modalBlocking');
    } catch (error) {
    }
  };
  
  const blockedTimesErrorMessage =
    blockedTimesError?.graphQLErrors?.[0]?.message || blockedTimesError?.message;

  const renderBlockedTimesMutationError = () =>
    blockedTimesErrorMessage ? (
      <div role="alert" className={classes.blockedTimesErrorBanner}>
        <span className="regularSmall">{blockedTimesErrorMessage}</span>
      </div>
    ) : null;

  const handleDeleteBlockedTime = async (blockedTimeOrId, event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!deleteBlockedTime) {
      console.warn("[Booking] deleteBlockedTime handler missing (prop not passed from CalendarPage)");
      return;
    }

    try {
      setIsDeleting(true);
      await deleteBlockedTime(blockedTimeOrId);
    } catch (error) {
      console.error("[Booking] delete blocked time failed:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  }

  const handleBackdropClick = () => {
    setShowConfirmation(false);
    setModalClass('modalBlocking');
    setIsModalOpen(!isModalOpen);
    setStartTime(null);
    setEndTime(null);
    setIsFullDay(false);
    setIsNotAvailable(false);
  }

  const handleModalClose = () => {
    setIsModalOpen(!isModalOpen);
  }
  
  const formatDateToGerman = (dateStr) => {
    const [day, month, year] = dateStr.split('.');
    const date = new Date(year, month - 1, day);
    
    const dayName = date.toLocaleDateString('de-DE', { weekday: 'long' });
    const monthName = date.toLocaleDateString('de-DE', { month: 'long' });
    
    return `${dayName}, ${day}. ${monthName} ${year}`;
  };

  // New function to get studio availability for selected date
  const getStudioAvailabilityForDate = (dateStr) => {
    if (!dateStr || !availabilities?.length) return null;
    
    // Parse the date string (DD.MM.YYYY)
    const [day, month, year] = dateStr.split('.');
    const date = new Date(year, month - 1, day);
    
    // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    // Convert to match API format (1 = Monday, ..., 7 = Sunday)
    const dayOfWeek = jsDayToApiDay(date.getDay());
    
    // Find availability for this day
    const dayAvailability = availabilities.find(av => apiDayToCanonicalDay(av.dayOfWeek) === dayOfWeek);
    
    if (!dayAvailability) {
      return null; // Studio not available on this day
    }
    
    return {
      startTime: dayAvailability.startTime,
      endTime: dayAvailability.endTime,
      startHour: dayAvailability.startHour,
      endHour: dayAvailability.endHour
    };
  };

  // Helper function to get available booking hours as array
  // const getAvailableBookingHours = (dateStr) => {
  //   const availability = getStudioAvailabilityForDate(dateStr);
  //   if (!availability) return [];
    
  //   const hours = [];
  //   for (let hour = availability.startHour; hour <= availability.endHour; hour++) {
  //     hours.push(`${hour.toString().padStart(2, '0')}:00`);
  //   }
  //   return hours;
  // };

  // const getOperatingHours = (bookings) => {
    
  //   if (!bookings.length) return { earliest: '', latest: '' };
    
  //   const times = bookings.reduce((acc, booking) => {
  //     acc.push(booking.startTime);
  //     acc.push(booking.endTime);
  //     return acc;
  //   }, []);

  //   const earliest = times.reduce((min, time) => 
  //     time < min ? time : min
  //   , times[0]);

  //   const latest = times.reduce((max, time) => 
  //     time > max ? time : max
  //   , times[0]);    

  //   return { earliest, latest };
  // };

  const generateTimesWithStatus = (bookings) => {
    const times = [];
    
    // Get studio availability for the selected date
    const studioAvailability = getStudioAvailabilityForDate(selectedBookingDate?.date);
    
    // If studio is not available on this day, return empty array
    if (!studioAvailability) {
      return times;
    }
    
    const availableStartHour = studioAvailability.startHour;
    const availableEndHour = studioAvailability.endHour;
    
    for (let hour = availableStartHour; hour <= availableEndHour; hour++) {
      const timeString = `${hour.toString().padStart(2, '0')}:00`;
      const isAvailable = !bookings.some(booking => {
        const startNum = parseInt(booking.startTime.split(':')[0]);
        const endNum = parseInt(booking.endTime.split(':')[0]);
        return hour >= startNum && hour < endNum;
      });
      
      times.push({ time: timeString, isAvailable });
    }
    return times;
  };

  const generateEndTimesWithStatus = (startTime, bookings) => {
    const startHour = startTime ? parseInt(startTime.split(':')[0]) : 9;
    const times = [];
    
    // Get studio availability for the selected date
    const studioAvailability = getStudioAvailabilityForDate(selectedBookingDate?.date);
    
    // If studio is not available on this day, return empty array
    if (!studioAvailability) {
      return times;
    }
    
    const availableEndHour = studioAvailability.endHour;
    const maxEndHour = Math.min(24, availableEndHour + 1); // Allow booking until studio closing time
    
    for (let hour = startHour + 1; hour <= maxEndHour; hour++) {
      const timeString = `${hour.toString().padStart(2, '0')}:00`;
      const isAvailable = !bookings.some(booking => {
        const bookingStart = parseInt(booking.startTime.split(':')[0]);
        const bookingEnd = parseInt(booking.endTime.split(':')[0]);
        return hour > bookingStart && hour <= bookingEnd;
      });
      
      times.push({
        time: timeString,
        isAvailable: isAvailable,
        isDisabled: false 
      });
    }
    return times;
  };

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    setEndTime(null);
    setIsNotAvailable(false);
  };

  const handleEndTimeChange = (e) => {
    const newEndTime = e.target.value;
    setEndTime(newEndTime);
  
    const hasConflict = bookings.some(booking => {
      const bookingStart = parseInt(booking.startTime.split(':')[0]);
      const bookingEnd = parseInt(booking.endTime.split(':')[0]);
      const selectedStart = parseInt(startTime.split(':')[0]);
      const selectedEnd = parseInt(newEndTime.split(':')[0]);
  
      return (
        (selectedStart < bookingEnd && selectedEnd > bookingStart) || 
        (selectedStart === bookingStart && selectedEnd > bookingStart) ||
        (selectedEnd === bookingEnd && selectedStart < bookingEnd)
      );
    });
  
    setIsNotAvailable(hasConflict);
  };

  const handleFullDayToggle = (e) => {
    const isChecked = e.target.checked;
    setIsFullDay(isChecked);
    
    if (isChecked) {
      setStartTime('00:00');
      setEndTime('23:59');
      setIsNotAvailable(false);
    } else {
      setStartTime(null);
      setEndTime(null);
    }
  };

  const handleCancel = () => {
    setModalClass('modalBlocking');
    setShowConfirmation(false);
  };

  const availableStartTimes = generateTimesWithStatus(bookings);
  const availableEndTimes = generateEndTimesWithStatus(startTime, bookings);
  
  const handleRestOfDayBlocking = async () => {
    // Get the currently selected date
    const [day, month, year] = selectedBookingDate.date.split('.');
    const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    
    // Sort bookings by startTime to ensure we process them in order
    const sortedBookings = [...bookings].sort((a, b) => 
      a.startTime.localeCompare(b.startTime)
    );
    
    // Generate time slots to block (all free periods)
    const timeSlots = [];
    
    // Add block from start of day (00:00) to first booking if needed
    if (sortedBookings.length > 0) {
      const firstBookingStart = sortedBookings[0].startTime;
      if (firstBookingStart !== "00:00") {
        timeSlots.push({
          start: "00:00",
          end: firstBookingStart
        });
      }
    } else {
      // If no bookings, block the entire day
      timeSlots.push({
        start: "00:00", 
        end: "23:59"
      });
    }
    
    // Add blocks between bookings
    for (let i = 0; i < sortedBookings.length - 1; i++) {
      const currentBookingEnd = sortedBookings[i].endTime;
      const nextBookingStart = sortedBookings[i + 1].startTime;
      
      // Only add a block if there's a gap
      if (currentBookingEnd !== nextBookingStart) {
        timeSlots.push({
          start: currentBookingEnd,
          end: nextBookingStart
        });
      }
    }
    
    // Add block from last booking to end of day (23:59) if needed
    if (sortedBookings.length > 0) {
      const lastBookingEnd = sortedBookings[sortedBookings.length - 1].endTime;
      if (lastBookingEnd !== "24:00" && lastBookingEnd !== "23:59") {
        timeSlots.push({
          start: lastBookingEnd,
          end: "23:59"
        });
      }
    }
        
    try {
      // Create a blocked time for each slot
      for (const slot of timeSlots) {
        // Adjust end time if it's 24:00
        const adjustedEndTime = slot.end === "24:00" ? "23:59" : slot.end;
        
        const blockedTimeInput = {
          studioID: selectedStudioId,
          title: `Block ${slot.start}-${adjustedEndTime}`,
          startDateTime: new Date(`${formattedDate}T${slot.start}`).toISOString(),
          endDateTime: new Date(`${formattedDate}T${adjustedEndTime}`).toISOString(),
          isFullDay: false
        };
        
        await createBlockedTime(blockedTimeInput);
      }
      
      // Reset UI on success
      setIsModalOpen(false);
      setStartTime(null);
      setEndTime(null);
      setIsFullDay(false);
      setIsNotAvailable(false);
      setShowConfirmation(false);
      setModalClass('modalBlocking');
    } catch (error) {
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <div className={classes.loadingSpinner}></div>
      </div>
    );
  }

  if (error) {
    return <div className={classes.booking}>Buchungen konnten nicht geladen werden.</div>;
  }

  if (!selectedBookingDate) {
    return (
      <div className={classes.booking}>
        <p>Bitte wähle einen Tag aus</p>
        {renderBlockedTimesMutationError()}
        <div 
          style={{display: !blockedTimeItems || blockedTimeItems.length === 0 ? "none" : "flex" }} 
          className={classes.blockedTimeList}
        >
          {blockedTimeItems.map(blockedTime => {
            const startDate = new Date(blockedTime.startDateTime);
            const endDate = new Date(blockedTime.endDateTime);
            const isMultiDay = startDate.toDateString() !== endDate.toDateString();
            
            return (
              <div key={blockedTime.id} className={classes.blockedTimeContainer}>
                <div className={classes.blockedTimeHeader}>
                  <h4 className="regularSmall">{blockedTime.title || "Blockierte Zeit"}</h4>
                  <div className={classes.blockedTime}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M6.75056 3.37944V7.69915M6.60022 7.41871L9.80022 9.14004M13 6.98047C13 10.2942 10.3137 12.9805 7 12.9805C3.68629 12.9805 1 10.2942 1 6.98047C1 3.66676 3.68629 0.980469 7 0.980469C10.3137 0.980469 13 3.66676 13 6.98047Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                    </svg>
                    <span className="regularXs">
                      {blockedTime.isFullDay
                        ? (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}` 
                          : `${format(startDate, 'dd.MM.yyyy')} - Ganzer Tag`)
                        : (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy HH:mm')} - ${format(endDate, 'dd.MM.yyyy HH:mm')} Uhr`
                          : `${format(startDate, 'dd.MM.yyyy')} ${format(startDate, 'HH:mm')} - ${format(endDate, 'HH:mm')} Uhr`)}
                    </span>
                  </div>
                </div>
                <button 
                  type="button"
                  className={`${classes.deleteButton} semiboldXs`}
                  onClick={(e) => handleDeleteBlockedTime(blockedTime, e)}
                  disabled={isDeleting}
                >
                  Löschen 
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Check if studio is available on the selected day
  const studioAvailability = getStudioAvailabilityForDate(selectedBookingDate?.date);
  
  if (!studioAvailability && selectedBookingDate) {
    return (
      <div className={classes.booking}>
        <header className={classes.bookingHeader}>
          <h2 className="titleSemibold">{formatDateToGerman(selectedBookingDate?.date)}</h2>
        </header>
        <header className={classes.bookingHeaderPhone}>
          <h2 className="titleSemibold3">{formatDateToGerman(selectedBookingDate?.date)}</h2>
          <button className={classes.closeButton} onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
            </svg>
          </button>
        </header>
        <div className={classes.emptyContainer}>
          <div className={classes.emptyStateContainer}>
            <div className={classes.emptyStateBoxContainer}>
              <div className={classes.emptyStateBox} />
              <div className={classes.emptyStateBox} />
              <div className={classes.emptyStateBox} />
            </div>
            <div className={classes.emptyStateContent}>
              <h2 className="titleSemibold">Studio geschlossen</h2>
              <p className="mediumLarge">Das Studio ist an diesem Tag nicht verfügbar</p>
            </div>
          </div>
        </div>
        {renderBlockedTimesMutationError()}
        <div 
          style={{display: !blockedTimeItems || blockedTimeItems?.length === 0 ? "none" : "flex" }} 
          className={classes.blockedTimeList}
        >
          {blockedTimeItems?.map(blockedTime => {
            const startDate = new Date(blockedTime?.startDateTime);
            const endDate = new Date(blockedTime?.endDateTime);
            const isMultiDay = startDate?.toDateString() !== endDate?.toDateString();
            
            return (
              <div key={blockedTime.id} className={classes.blockedTimeContainer}>
                <div className={classes.blockedTimeHeader}>
                  <h4 className="regularSmall">{blockedTime.title || "Blockierte Zeit"}</h4>
                  <div className={classes.blockedTime}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M6.75056 3.37944V7.69915M6.60022 7.41871L9.80022 9.14004M13 6.98047C13 10.2942 10.3137 12.9805 7 12.9805C3.68629 12.9805 1 10.2942 1 6.98047C1 3.66676 3.68629 0.980469 7 0.980469C10.3137 0.980469 13 3.66676 13 6.98047Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                    </svg>
                    <span className="regularXs">
                      {blockedTime.isFullDay
                        ? (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}` 
                          : `${format(startDate, 'dd.MM.yyyy')} - Ganzer Tag`)
                        : (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy HH:mm')} - ${format(endDate, 'dd.MM.yyyy HH:mm')} Uhr`
                          : `${format(startDate, 'dd.MM.yyyy')} ${format(startDate, 'HH:mm')} - ${format(endDate, 'HH:mm')} Uhr`)}
                    </span>
                  </div>
                </div>
                <button 
                  type="button"
                  className={`${classes.deleteButton} semiboldXs`}
                  onClick={(e) => handleDeleteBlockedTime(blockedTime, e)}
                  disabled={isDeleting}
                >
                  Löschen 
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={classes.booking}>
      {(bookings?.length === 0) && (blockedTimeItems?.length === 0) ? (
        <>
          <header className={classes.bookingHeader}>
            <h2 className="titleSemibold">{formatDateToGerman(selectedBookingDate?.date)}</h2>
            <button onClick={handleModalOpen} className={`${classes.blockTimeButton} semiboldXs`}>Zeit blockieren</button>
          </header>
          <header className={classes.bookingHeaderPhone}>
            <h2 className="titleSemibold3">{formatDateToGerman(selectedBookingDate?.date)}</h2>
            <button className={classes.closeButton} onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
              </svg>
            </button>
          </header>
          <div className={classes.emptyContainer}>
            <div className={classes.emptyStateContainer}>
                <div className={classes.emptyStateBoxContainer}>
                  <div className={classes.emptyStateBox} />
                  <div className={classes.emptyStateBox} />
                  <div className={classes.emptyStateBox} />
              </div>
              <div className={classes.emptyStateContent}>
                  <h2 className="titleSemibold">
                    {hasStudios ? (
                      "Noch keine Buchungen"
                    ) : (
                      "Noch kein Studio"
                    )}
                  </h2>
                  <p className="mediumLarge">
                    {hasStudios ? (
                      "Verbessere dein Studio und werde gebucht"
                    ) : (
                      "Erstelle ein Studio und werde gebucht"
                    )}
                  </p>
                  {hasStudios ? (
                    <Link style={{ color: 'white' }} to="/services-studios" className={`${classes.improveButton} regularSmall`}>
                      Studio verbessern
                    </Link>
                  ) : (
                    <Link style={{ color: 'white' }} to="/studio-erstellen" className={`${classes.improveButton} regularSmall`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.0317H1V7.95539H17V9.0317Z" fill="white"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.5V16.5H8.45801V0.5H9.53433Z" fill="white"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.0317H1V7.95539H17V9.0317Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.5V16.5H8.45801V0.5H9.53433Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                      </svg>
                      Studio erstellen
                    </Link>
                  )}
              </div>
            </div>
          </div>
          {renderBlockedTimesMutationError()}
          <div 
          style={{display: !blockedTimeItems || blockedTimeItems?.length === 0 ? "none" : "flex" }} 
          className={classes.blockedTimeList}
        >
          {blockedTimeItems.map(blockedTime => {
            const startDate = new Date(blockedTime?.startDateTime);
            const endDate = new Date(blockedTime?.endDateTime);
            const isMultiDay = startDate?.toDateString() !== endDate?.toDateString();
            
            return (
              <div key={blockedTime.id} className={classes.blockedTimeContainer}>
                <div className={classes.blockedTimeHeader}>
                  <h4 className="regularSmall">{blockedTime.title || "Blockierte Zeit"}</h4>
                  <div className={classes.blockedTime}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M6.75056 3.37944V7.69915M6.60022 7.41871L9.80022 9.14004M13 6.98047C13 10.2942 10.3137 12.9805 7 12.9805C3.68629 12.9805 1 10.2942 1 6.98047C1 3.66676 3.68629 0.980469 7 0.980469C10.3137 0.980469 13 3.66676 13 6.98047Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                    </svg>
                    <span className="regularXs">
                      {blockedTime.isFullDay
                        ? (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}` 
                          : `${format(startDate, 'dd.MM.yyyy')} - Ganzer Tag`)
                        : (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy HH:mm')} - ${format(endDate, 'dd.MM.yyyy HH:mm')} Uhr`
                          : `${format(startDate, 'dd.MM.yyyy')} ${format(startDate, 'HH:mm')} - ${format(endDate, 'HH:mm')} Uhr`)}
                    </span>
                  </div>
                </div>
                <button 
                  type="button"
                  className={`${classes.deleteButton} semiboldXs`}
                  onClick={(e) => handleDeleteBlockedTime(blockedTime, e)}
                  disabled={isDeleting}
                >
                  Löschen 
                </button>
              </div>
            );
          })}
          </div>
        </>
      ) : (
        <>
        <header className={classes.bookingHeader}>
          <h2 className="titleSemibold">{formatDateToGerman(selectedBookingDate?.date)}</h2>
          <button onClick={handleModalOpen} className={`${classes.blockTimeButton} semiboldXs`}>Zeit blockieren</button>
        </header>
        <header className={classes.bookingHeaderPhone}>
          {/* Not sure about this h2 dimensions in phone */}
          <h2 className="semiboldLarge">{formatDateToGerman(selectedBookingDate?.date)}</h2>
          <button className={classes.closeButton} onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
            </svg>
          </button>
        </header>
        <div className={classes.bookingTime}>
          <div className={classes.bookingTimeHeader}>
            <h4 className="regularXxs">Betriebszeit</h4>
            {/* <button onClick={() => setShowBlockingPhone(true)} className={`${classes.timeBlockButton} semiboldXs`}>
              Zeit blockieren
            </button> */}
          </div>
            {studioAvailability && (
              <div className={`${classes.timeBlock} regularXxs`} style={{ marginBottom: "8px", color: "var(--dark-grey-2)" }}>
                {studioAvailability.startTime} - {studioAvailability.endTime} Uhr
              </div>
            )}
            {/* {bookings?.length > 0 && (
              <div className={`${classes.timeBlock} regularXxs`}>
                Aktive Buchungen: {getOperatingHours(bookings)?.earliest} - {getOperatingHours(bookings)?.latest} Uhr
              </div>
            )} */}
        </div>
        <div className={classes.bookingList}>
          {bookings.map((booking, index) => (
            <BookingContainer 
              key={index} 
              booking={booking} 
              date={formatDateToGerman(selectedBookingDate?.date)}
              onBookingUpdate={onBookingUpdate}
            />
          ))}
        </div>
        {renderBlockedTimesMutationError()}
        <div 
          style={{display: !blockedTimeItems || blockedTimeItems.length === 0 ? "none" : "flex" }} 
          className={classes.blockedTimeList}
        >
          {blockedTimeItems?.map(blockedTime => {
            const startDate = new Date(blockedTime?.startDateTime);
            const endDate = new Date(blockedTime?.endDateTime);
            const isMultiDay = startDate?.toDateString() !== endDate?.toDateString();
            
            return (
              <div key={blockedTime?.id} className={classes.blockedTimeContainer}>
                <div className={classes.blockedTimeHeader}>
                  <h4 className="regularSmall">{blockedTime?.title || "Blockierte Zeit"}</h4>
                  <div className={classes.blockedTime}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M6.75056 3.37944V7.69915M6.60022 7.41871L9.80022 9.14004M13 6.98047C13 10.2942 10.3137 12.9805 7 12.9805C3.68629 12.9805 1 10.2942 1 6.98047C1 3.66676 3.68629 0.980469 7 0.980469C10.3137 0.980469 13 3.66676 13 6.98047Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                    </svg>
                    <span className="regularXs">
                      {blockedTime?.isFullDay
                        ? (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy')} - ${format(endDate, 'dd.MM.yyyy')}` 
                          : `${format(startDate, 'dd.MM.yyyy')} - Ganzer Tag`)
                        : (isMultiDay 
                          ? `${format(startDate, 'dd.MM.yyyy HH:mm')} - ${format(endDate, 'dd.MM.yyyy HH:mm')} Uhr`
                          : `${format(startDate, 'dd.MM.yyyy')} ${format(startDate, 'HH:mm')} - ${format(endDate, 'HH:mm')} Uhr`)}
                    </span>
                  </div>
                </div>
                <button 
                  type="button"
                  className={`${classes.deleteButton} semiboldXs`}
                  onClick={(e) => handleDeleteBlockedTime(blockedTime, e)}
                  disabled={isDeleting}
                >
                  Löschen 
                </button>
              </div>
            );
          })}
        </div>
        {/* {showBlockingPhone && (
          <div className={classes.timeBlockingPhone}>
            <div className={classes.modalHeaderPhone}> 
              <button className={classes.backButton} onClick={() => setShowBlockingPhone(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="17" viewBox="0 0 9 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M0 8.01585L7.90197 0L8.95756 1.07081L2.07028 8.05734L9 15.6727L7.90306 16.6999L0.0119278 8.02795L0 8.01585Z" fill="black"/>
                </svg>
              </button>
              <h2 className="titleSemibold3">Zeit blockieren</h2>
              <button onClick={() => {onClose(); setShowBlockingPhone(false)}} className={classes.closeButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </button>
            </div>
            <div className={classes.modalContent}>
                <div className={classes.timeContainer}>
                  <div className={classes.bookedTimeContainer}>
                    <span style={{color: "var(--dark-grey-2)", marginBottom: "5px"}} className="regularXxs">Betriebszeit</span>
                    {bookings.map((booking, index) => (
                      <div key={index} className={classes.bookedTime}>
                        <span className="regularXxs">{booking.startTime} - {booking.endTime} Uhr</span>
                      </div>
                    ))}
                  </div>
                  <span className={`${classes.date} semiboldXs`}>{selectedBookingDate.date}</span>
                </div>
                <input type="text" placeholder="Titel (optional)" className={classes.input} />
                <div className={classes.timeSelectionContainer}>
                  <div className={classes.wholeDayContainer} style={{borderBottom: isFullDay ? "none" : "1px solid var(--light-grey-1)"}}>
                    <h4 className="regularMiddle">Ganztägig</h4>
                    <div className={classes.switchContainer}>
                      <label className={classes.switch}>
                          <input 
                              type="checkbox"
                              checked={isFullDay}
                              onChange={handleFullDayToggle}
                          />
                              <span className={classes.slider}></span>
                      </label>
                    </div>
                  </div>
                  {!isFullDay && (
                  <div className={classes.timeSelection}>
                    <div className={classes.timeSelectionItem}>
                      <span className={`regularMiddle ${classes.selectionText}`}>Start:</span>
                      <span 
                        className={classes.timeSelectionText} 

                        style={{ color: startTime ? "var(--dark-grey)" : "var(--grey-2)" }}
                      >
                        {startTime || "09:00"}
                      </span>
                      <select 
                        className={classes.timeInput}
                        onChange={handleStartTimeChange}
                        disabled={isFullDay}
                        value={startTime || ""}
                      >
                        {availableStartTimes.map(({ time, isAvailable }) => (
                          <option 
                            key={time} 
                            value={time}
                            disabled={!isAvailable}
                            style={{ color: isAvailable ? 'var(--dark-grey)' : 'var(--grey-2)' }}
                          >
                            {time}
                          </option>
                        ))}
                      </select>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                        <line y1="-0.5" x2="8.80883" y2="-0.5" transform="matrix(0.708363 0.705848 -0.708363 0.705848 0.351562 1.18555)" stroke="#1F1F1F"/>
                        <line y1="-0.5" x2="9.18451" y2="-0.5" transform="matrix(0.736004 -0.676977 0.679582 0.7336 6.58984 7.40039)" stroke="#1F1F1F"/>
                      </svg>
                    </div>
                    <span style={{color: "var(--dark-grey-3)"}} className={`regularXxs ${classes.selectionText}`}>bis</span>
                    <div className={classes.timeSelectionItem}>
                      <span className={`regularMiddle ${classes.selectionText}`}>Ende:</span>
                      <span 
                        className={classes.timeSelectionText}
                        style={{ color: endTime ? "var(--dark-grey)" : "var(--grey-2)" }}
                      >
                        {endTime || "09:00"}
                      </span>
                      <select 
                        className={classes.timeInput}
                        onChange={handleEndTimeChange}
                        disabled={isFullDay || !startTime}
                        value={endTime || ""}
                      >
                        {availableEndTimes.map(({ time, isAvailable, isDisabled }) => (
                          <option 
                            key={time} 
                            value={time}
                            disabled={isDisabled || !isAvailable}
                            style={{ 
                              color: isDisabled ? 'var(--grey-2)' : 
                                    isAvailable ? 'var(--dark-grey)' : 
                                    'var(--grey-2)'
                            }}
                          >
                            {time}
                          </option>
                        ))}
                      </select>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                        <line y1="-0.5" x2="8.80883" y2="-0.5" transform="matrix(0.708363 0.705848 -0.708363 0.705848 0.351562 1.18555)" stroke="#1F1F1F"/>
                        <line y1="-0.5" x2="9.18451" y2="-0.5" transform="matrix(0.736004 -0.676977 0.679582 0.7336 6.58984 7.40039)" stroke="#1F1F1F"/>
                      </svg>
                    </div>
                  </div>
                  )}
                </div>
                {isNotAvailable && (
                <div className={classes.infosContainer}>
                  <div className={classes.infosIcon} >
                    <svg xmlns="http://www.w3.org/2000/svg" width="2" height="12" viewBox="0 0 2 12" fill="none">
                      <path d="M1.8425 1.37691e-08L1.8425 8.37989L0.4425 8.37989L0.442499 1.36161e-07L1.8425 1.37691e-08ZM1.125 10.324C1.35833 10.324 1.5625 10.4078 1.7375 10.5754C1.9125 10.743 2 10.9385 2 11.162C2 11.3966 1.9125 11.5922 1.7375 11.7486C1.5625 11.9162 1.35833 12 1.125 12C0.88 12 0.675833 11.9162 0.5125 11.7486C0.3375 11.5922 0.25 11.3966 0.25 11.162C0.25 10.9385 0.3375 10.743 0.5125 10.5754C0.675833 10.4078 0.88 10.324 1.125 10.324Z" fill="#77808A"/>
                    </svg>
                  </div>
                  <p style={{color: "var(--dark-grey-4)"}} className="semiboldXxs">
                    Du hast aktive Buchungen. Diese Zeiten können erst nach Stornierung blockiert werden.
                  </p>
                </div>
                )}
                <footer className={classes.modalFooter}>
                  <button 
                    className={`${classes.modalButton} mediumLarge`} 
                    disabled={isNotAvailable || !startTime || !endTime}
                    onClick={handleConfirmation}
                  >
                    Zeit blockieren
                  </button>
                </footer>
              </div>
              <Modal open={isModalOpen} className={modalClass} onClick={(e) => e.stopPropagation()}>
                <div className={classes.smallModalContent} onClick={(e) => e.stopPropagation()}>
                  <h1 className="titleSemibold3">Du hast aktive Buchungen</h1>
                  <p className={classes.modalText}>
                    Du musst vorher stornieren, bevor du diese Zeit blockieren kannst. Der restliche Tag ist jedoch blockierbar.
                  </p>
                  <div className={classes.smallModalButtons}>
                    <button
                      className={`${classes.smallModalButton} ${classes.blockButton} mediumLarge`} 
                      onClick={handleRestOfDayBlocking}
                    >
                      Restlichen Tag blockieren
                    </button>
                    <button 
                      className={`${classes.smallModalButton} ${classes.cancelButton} mediumLarge`} 
                      onClick={handleCancel}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              </Modal>
          </div>
        )} */}
       </>
      )}
            
        <Modal 
          open={isModalOpen} 
          className={modalClass}
          onBackdropClick={handleModalClose}
        >
          <div ref={modalRef} className={classes.modalContainer}>
            {!showConfirmation ? (
            <>
            <div className={classes.modalHeader}> 
              <h2 className="titleSemibold3">Zeit blockieren</h2>
              <button onClick={handleBackdropClick} className={classes.closeButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </button>
            </div>
            <div className={classes.modalContent}>
              <div className={classes.timeContainer}>
                <div className={classes.bookedTimeContainer}>
                  <span style={{color: "var(--dark-grey-2)", marginBottom: "5px"}} className="regularXxs">Betriebszeit</span>
                  {bookings.map((booking, index) => (
                    <div key={index} className={classes.bookedTime}>
                      <span className="regularXxs">{booking.startTime} - {booking.endTime} Uhr</span>
                    </div>
                  ))}
                </div>
                <span className={`${classes.date} semiboldXs`}>{selectedBookingDate.date}</span>
              </div>
              <input type="text" placeholder="Titel (optional)" className={classes.input} />
              <div className={classes.timeSelectionContainer}>
                <div className={classes.wholeDayContainer} style={{borderBottom: isFullDay ? "none" : "1px solid var(--light-grey-1)"}}>
                  <h4 className="regularMiddle">Ganztägig</h4>
                  <div className={classes.switchContainer}>
                    <label className={classes.switch}>
                        <input 
                            type="checkbox"
                            checked={isFullDay}
                            onChange={handleFullDayToggle}
                        />
                            <span className={classes.slider}></span>
                    </label>
                  </div>
                </div>
                {!isFullDay && (
                <div className={classes.timeSelection}>
                  <div className={classes.timeSelectionItem}>
                    <span className={`regularMiddle ${classes.selectionText}`}>Start:</span>
                    <span 
                      className={classes.timeSelectionText} 
                      style={{ color: startTime ? "var(--dark-grey)" : "var(--grey-2)" }}

                    >
                      {startTime || availableStartTimes.find(t => t.isAvailable)?.time || "00:00"}
                    </span>
                    <select 
                      className={classes.timeInput}
                      onChange={handleStartTimeChange}
                      disabled={isFullDay}
                      value={startTime || ""}
                    >
                      <option value="" disabled hidden>
                        {availableStartTimes?.find(t => t?.isAvailable)?.time || "00:00"}
                      </option>
                      {availableStartTimes?.map(({ time, isAvailable }) => (
                        <option 
                          key={time} 
                          value={time}
                          disabled={!isAvailable}
                          style={{ color: isAvailable ? 'var(--dark-grey)' : 'var(--grey-2)' }}
                        >
                          {time}
                        </option>
                      ))}
                    </select>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                      <line y1="-0.5" x2="8.80883" y2="-0.5" transform="matrix(0.708363 0.705848 -0.708363 0.705848 0.351562 1.18555)" stroke="#1F1F1F"/>
                      <line y1="-0.5" x2="9.18451" y2="-0.5" transform="matrix(0.736004 -0.676977 0.679582 0.7336 6.58984 7.40039)" stroke="#1F1F1F"/>
                    </svg>
                  </div>
                  <span style={{color: "var(--dark-grey-3)"}} className={`regularXxs ${classes.selectionText}`}>bis</span>
                  <div className={classes.timeSelectionItem}>
                    <span className={`regularMiddle ${classes.selectionText}`}>Ende:</span>
                    <span 
                      className={classes.timeSelectionText}
                      style={{ color: endTime ? "var(--dark-grey)" : "var(--grey-2)" }}

                    >
                      {endTime || "09:00"}
                    </span>
                    <select 
                      className={classes.timeInput}
                      onChange={handleEndTimeChange}
                      disabled={isFullDay || !startTime}
                      value={endTime || ""}
                    >
                      {availableEndTimes.map(({ time, isAvailable, isDisabled }) => (
                        <option 
                          key={time} 
                          value={time}
                          disabled={isDisabled || !isAvailable}
                          style={{ 
                            color: isDisabled ? 'var(--grey-2)' : 
                                  isAvailable ? 'var(--dark-grey)' : 
                                  'var(--grey-2)'
                          }}
                        >
                          {time}
                        </option>
                      ))}
                    </select>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                      <line y1="-0.5" x2="8.80883" y2="-0.5" transform="matrix(0.708363 0.705848 -0.708363 0.705848 0.351562 1.18555)" stroke="#1F1F1F"/>
                      <line y1="-0.5" x2="9.18451" y2="-0.5" transform="matrix(0.736004 -0.676977 0.679582 0.7336 6.58984 7.40039)" stroke="#1F1F1F"/>
                    </svg>
                  </div>
                </div>
                )}
              </div>
              {isNotAvailable && (
              <div className={classes.infosContainer}>
                <div className={classes.infosIcon} >
                  <svg xmlns="http://www.w3.org/2000/svg" width="2" height="12" viewBox="0 0 2 12" fill="none">
                    <path d="M1.8425 1.37691e-08L1.8425 8.37989L0.4425 8.37989L0.442499 1.36161e-07L1.8425 1.37691e-08ZM1.125 10.324C1.35833 10.324 1.5625 10.4078 1.7375 10.5754C1.9125 10.743 2 10.9385 2 11.162C2 11.3966 1.9125 11.5922 1.7375 11.7486C1.5625 11.9162 1.35833 12 1.125 12C0.88 12 0.675833 11.9162 0.5125 11.7486C0.3375 11.5922 0.25 11.3966 0.25 11.162C0.25 10.9385 0.3375 10.743 0.5125 10.5754C0.675833 10.4078 0.88 10.324 1.125 10.324Z" fill="#77808A"/>
                  </svg>
                </div>
                <p style={{color: "var(--dark-grey-4)"}} className="semiboldXxs">
                  Du hast aktive Buchungen. Diese Zeiten können erst nach Stornierung blockiert werden.
                </p>
              </div>
              )}
            </div>
            <footer className={classes.modalFooter}>
              <button 
                className={`${classes.modalButton} mediumLarge`} 
                disabled={isNotAvailable || !startTime || !endTime}
                onClick={handleConfirmation}
              >
                Zeit blockieren
              </button>
            </footer>
            </>
            ) : (
              <div className={classes.smallModalContent}>
                <h1 className="titleSemibold3">Du hast aktive Buchungen</h1>
                <p className={classes.modalText}>
                  Du musst vorher stornieren, bevor du diese Zeit blockieren kannst. Der restliche Tag ist jedoch blockierbar.
                </p>
                <div className={classes.smallModalButtons}>
                  <button 
                    className={`${classes.smallModalButton} ${classes.blockButton} mediumLarge`} 
                    onClick={handleRestOfDayBlocking}
                  >
                    Restlichen Tag blockieren
                  </button>
                  <button 
                    className={`${classes.smallModalButton} ${classes.cancelButton} mediumLarge`} 
                    onClick={handleCancel}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
    </div>
  );
};

export default Booking;
